import type { TBookingCart } from '@providers/booking/types'
import { isCheckInOutTimeEnabled, isVetEnabled } from 'components-ui/src/shared/location'
import { isInRange } from 'components-ui/src/shared/dates'
import { ClientAssetUsageService } from 'service-api/src/services/client_asset_usage.service'
import { ClientAssetService } from 'service-api/src/services/client_asset.service'
import type {
  BookingReservationClientDto,
  FlexBookingConfClientDto,
  LocationBookingConfClientDto,
  RestrictionClientDto,
  UpdateBookingOrderClientInput,
} from 'service-api/src/types/api'
import type { GenericSimpleBag } from 'service-api/src/types/general'

export const buildCheckInOutOrderUpdate = (
  values: { checkInTime?: string; checkOutTime?: string },
  reservation: BookingReservationClientDto
): Pick<UpdateBookingOrderClientInput, 'data'> => {
  if (!isCheckInOutTimeEnabled(reservation.locationBookingConf!)) {
    return {}
  }
  const ci = (values.checkInTime || '').trim()
  const co = (values.checkOutTime || '').trim()
  if (!ci && !co) {
    return {}
  }
  return {
    data: { ...(ci ? { checkInTime: ci } : {}), ...(co ? { checkOutTime: co } : {}) },
  }
}

export const buildOrderInput = (
  values: GenericSimpleBag,
  reservation: BookingReservationClientDto,
  inputCardGroups: FlexBookingConfClientDto[][],
  cart?: Pick<TBookingCart, 'checkInTime' | 'checkOutTime'>
): UpdateBookingOrderClientInput => {
  const data: UpdateBookingOrderClientInput = {
    userData: {
      firstName: values.firstName,
      lastName: values.lastName,
      phone: values.phone,
      email: values.email,
      data: {
        mailingAddress: {
          streetAddress: values.streetAddress,
          streetAddress2: values.streetAddress2.trim() === '' ? null : values.streetAddress2,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode,
          country: values.country,
        },
      },
    },
  }

  const checkInOut = buildCheckInOutOrderUpdate(
    {
      checkInTime: values.checkInTime || cart?.checkInTime,
      checkOutTime: values.checkOutTime || cart?.checkOutTime,
    },
    reservation
  )
  if (checkInOut.data) {
    data.data = { ...(data.data || {}), ...checkInOut.data }
  }

  if (isVetEnabled(reservation.locationBookingConf!)) {
    let { vetName: vn, vetPhone: vp } = values
    vn = vn.trim()
    vp = vp.trim()

    if (vn || vp) {
      const vet = { ...(vn ? { name: vn } : {}), ...(vp ? { phone: vp } : {}) }
      data.data = { ...(data.data || {}), ...vet }
    }
  }

  const fieldNames = inputCardGroups
    .reduce((acc, groups) => acc.concat(groups.map(({ name }) => name)), [] as (string | undefined)[])
    .filter(Boolean) as string[]

  fieldNames.forEach((fieldName) => {
    data.flexBookingConfs ||= {}
    data.flexBookingConfs[fieldName] = [{ value: values[fieldName]?.trim() || '' }]
  })

  return data
}

export const inputCardsFilter = (bookingConf: FlexBookingConfClientDto) => {
  return (
    bookingConf.type === 'FIELD' &&
    bookingConf.target === 'ORDER' &&
    (bookingConf.step === 'BOOKING' || bookingConf.step === 'ALL')
  )
}

export const inputCardGroupsBuilder = (bookingConf: FlexBookingConfClientDto[]) => {
  const groups =
    bookingConf?.filter(inputCardsFilter).reduce<Record<string, FlexBookingConfClientDto[]>>((acc, bc) => {
      const group = bc.group || ''
      acc[group] ||= []
      acc[group].push(bc)
      return acc
    }, {}) || {}
  return Object.values(groups)
}

export const createAsset = ({ accountName, file, orderId }: { accountName: string; file: File; orderId?: string }) =>
  ClientAssetService.create(accountName, 'USER', 'RECORD', file).then((createdAsset) =>
    ClientAssetUsageService.create(accountName, createdAsset.id!, {
      orderId,
      type: 'ORDER_ATTACHMENT',
    })
  )

export const getOfferIdFromErrorMsg = (errorMsg: string) => {
  const match = errorMsg.match(/(c\w{24,})/)
  return match ? match[1] : null
}

type TPeriod = NonNullable<RestrictionClientDto['periods']>[number]

const isSpecialPeriod = (period: TPeriod) => !!period.startDate || !!period.endDate

const coversDate = (period: TPeriod, date?: string) => !date || isInRange(date, [period])

export const resolveOperationHours = (
  operationRestrictions: RestrictionClientDto[] | undefined,
  date?: string
): RestrictionClientDto[] | undefined => {
  if (!operationRestrictions?.length || !date) {
    return operationRestrictions
  }
  const hasSpecialForDate = operationRestrictions.some((restriction) =>
    (restriction.periods ?? []).some((period) => isSpecialPeriod(period) && coversDate(period, date))
  )
  if (!hasSpecialForDate) {
    return operationRestrictions
  }
  return (
    operationRestrictions
      .map((restriction) => ({
        ...restriction,
        periods: restriction.periods?.filter((period) => isSpecialPeriod(period) && coversDate(period, date)),
      }))
      // Keep any restriction that still has a special period for the date. Also keep closures
      // (available === false) regardless of whether they were left with periods, since they
      // must continue to block the date downstream. Only open (available) restrictions whose
      // periods filtered down to empty are dropped.
      .filter((restriction) => !!restriction.periods?.length || restriction.available === false)
  )
}

export const applyLimitHours = (
  restrictions: RestrictionClientDto[] | undefined,
  surchargeStrategy: NonNullable<LocationBookingConfClientDto['surchargeStrategy']> | undefined,
  date?: string,
  operationRestrictions?: RestrictionClientDto[]
): RestrictionClientDto[] | undefined => {
  const limitsHours = surchargeStrategy === 'SUGGESTED' || surchargeStrategy === 'ENFORCED'
  if (!limitsHours || !restrictions?.length) {
    return undefined
  }

  // Collect every check-hour period that applies to the selected date.
  const periodsForDate = restrictions.flatMap((restriction) =>
    (restriction.periods ?? []).filter((period) => coversDate(period, date))
  )

  // A special dated window (check OR operation) covering the date overrides regular hours
  // for that date. We consider special operation windows too, because a special date with
  // only an operation window (no special check window) must still suppress the regular
  // limit and fall through to the special operation hours.
  const hasSpecialCheckForDate = periodsForDate.some(isSpecialPeriod)
  const hasSpecialOperationForDate = (operationRestrictions ?? []).some((restriction) =>
    (restriction.periods ?? []).some((period) => isSpecialPeriod(period) && coversDate(period, date))
  )
  const hasSpecialForDate = hasSpecialCheckForDate || hasSpecialOperationForDate

  // If the special operation window governing the date is a closure (available === false),
  // the date is closed. Return undefined so the operation-hours fallback preserves the
  // closure rather than offering flagged check times on a closed date.
  const isSpecialOperationClosedForDate = (operationRestrictions ?? []).some(
    (restriction) =>
      restriction.available === false &&
      (restriction.periods ?? []).some((period) => isSpecialPeriod(period) && coversDate(period, date))
  )
  if (isSpecialOperationClosedForDate) {
    return undefined
  }

  // When a special window governs the date, only special check periods can win. When no
  // special window applies, the regular periods win.
  const isWinner = (period: TPeriod) => coversDate(period, date) && (!hasSpecialForDate || isSpecialPeriod(period))

  const winningPeriods = periodsForDate.filter(isWinner)
  const hasLimited = winningPeriods.some((period) => period.limitHoursEnabled === true)
  if (!hasLimited) {
    return undefined
  }

  const isWinningFlagged = (period: TPeriod) => isWinner(period) && period.limitHoursEnabled === true

  // Keep only the flagged winning periods, and drop any restriction left with none: an
  // empty periods array is treated downstream (getHoursByDate) as an unrestricted
  // full-day range, which would reintroduce all-day slots and defeat the limit.
  return restrictions
    .map((restriction) => ({
      ...restriction,
      periods: restriction.periods?.filter(isWinningFlagged),
    }))
    .filter((restriction) => !!restriction.periods?.length)
}
